import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Users,
  Activity,
  MapPin,
  Building2,
  TrendingUp,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { formatDate, formatDayMonth } from '../lib/dates';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_HIGHLIGHT_IMAGE =
  'https://images.unsplash.com/photo-1760896124502-048eaabb0004?q=85&w=800&auto=format&fit=crop';

const getTotalBeneficiaries = (stats) =>
  Number(
    stats?.total?.beneficiarios ??
      stats?.total?.total_beneficiarios ??
      stats?.total?.total_beneficiados ??
      0
  ) || 0;

const getWeeklyBeneficiaries = (stats) =>
  Number(
    stats?.weekly?.beneficiarios ??
      stats?.weekly?.total_beneficiarios ??
      stats?.weekly?.total_beneficiados ??
      0
  ) || 0;

const getTotalActions = (stats) =>
  Number(
    stats?.total?.acciones ??
      stats?.total?.total_acciones ??
      0
  ) || 0;

const getWeeklyActions = (stats) =>
  Number(
    stats?.weekly?.acciones ??
      stats?.weekly?.total_acciones ??
      0
  ) || 0;

const getCoverageTerritoriesActive = (stats) =>
  Number(stats?.coverage?.territories_active ?? 0) || 0;

const getCoverageTerritoriesTotal = (stats) =>
  Number(stats?.coverage?.territories_total ?? 0) || 0;

const getCoverageDependenciesActive = (stats) =>
  Number(stats?.coverage?.dependencies_active ?? 0) || 0;

const getCoverageDependenciesTotal = (stats) =>
  Number(stats?.coverage?.dependencies_total ?? 0) || 0;

const getTerritoryBeneficiaries = (territoryStats = {}) =>
  Number(
    territoryStats.total_beneficiarios ??
      territoryStats.total_beneficiados ??
      0
  ) || 0;

const getTerritoryActivities = (territoryStats = {}) =>
  Number(territoryStats.total_activities ?? 0) || 0;

const getHighlightBeneficiaries = (item = {}) =>
  Number(
    item.beneficiarios ??
      item.total_beneficiarios ??
      item.total_beneficiados ??
      0
  ) || 0;

const getHighlightImage = (item = {}) => {
  if (!item.photo) return FALLBACK_HIGHLIGHT_IMAGE;
  return item.photo.startsWith('/api')
    ? `${process.env.REACT_APP_BACKEND_URL}${item.photo}`
    : item.photo;
};

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [weeklyTrend, setWeeklyTrend] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [territoriesStats, setTerritoriesStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, trendRes, highlightsRes, territoriesRes] = await Promise.all([
          axios.get(`${API}/stats/global`),
          axios.get(`${API}/stats/weekly-trend`),
          axios.get(`${API}/stats/highlights`),
          axios.get(`${API}/stats/by-territory`),
        ]);

        setStats(statsRes.data || null);
        setWeeklyTrend(Array.isArray(trendRes.data) ? trendRes.data : []);
        setHighlights(Array.isArray(highlightsRes.data) ? highlightsRes.data : []);
        setTerritoriesStats(territoriesRes.data || {});
      } catch (error) {
        console.error('Error fetching data:', error);
        setStats(null);
        setWeeklyTrend([]);
        setHighlights([]);
        setTerritoriesStats({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const territoryChartData = useMemo(() => {
    return Object.values(territoriesStats || {})
      .filter((territoryStat) => getTerritoryActivities(territoryStat) > 0)
      .sort(
        (a, b) => getTerritoryBeneficiaries(b) - getTerritoryBeneficiaries(a)
      )
      .slice(0, 8)
      .map((territoryStat) => {
        const fullName = territoryStat?.territory?.name || 'Territorio';
        return {
          name: fullName.length > 15 ? `${fullName.substring(0, 15)}...` : fullName,
          beneficiarios: getTerritoryBeneficiaries(territoryStat),
          actividades: getTerritoryActivities(territoryStat),
        };
      });
  }, [territoriesStats]);

  const normalizedWeeklyTrend = useMemo(() => {
    return weeklyTrend.map((item) => ({
      week: item.week || item.week_start || '',
      beneficiarios:
        Number(
          item.beneficiarios ??
            item.total_beneficiarios ??
            item.total_beneficiados ??
            0
        ) || 0,
      acciones:
        Number(
          item.acciones ??
            item.total_acciones ??
            0
        ) || 0,
    }));
  }, [weeklyTrend]);

  const territoriesTotal =
    getCoverageTerritoriesTotal(stats) || Object.keys(territoriesStats || {}).length || 0;

  const dependenciesTotal = getCoverageDependenciesTotal(stats);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E1]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#8B1C4A]/20" />
          <p className="text-slate-600">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F5F0E1]" data-testid="dashboard">
      <section className="relative bg-gradient-to-br from-[#8B1C4A] to-[#5a1130] text-white py-14 sm:py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://cdn.cdmx.gob.mx/apps/donalap/img/wallpaper_1.jpg')] bg-cover bg-center opacity-15" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-in-up pt-4 sm:pt-6">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-4 leading-tight">
              Territorios de Paz e Igualdad
            </h1>

            <p className="text-lg lg:text-xl text-white/80 mb-8 leading-relaxed">
              Rendición de cuentas semanal del Gobierno de la Ciudad de México en{' '}
              {territoriesTotal || 'múltiples'} territorios estratégicos.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <Link to="/territorios">
                <Button
                  className="w-full sm:w-auto bg-white text-[#8B1C4A] hover:bg-[#F5F0E1] font-semibold px-6 py-3"
                  data-testid="explore-territories-btn"
                >
                  Explorar Territorios
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>

              <Link to="/resumen">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 font-semibold px-6 py-3"
                  data-testid="weekly-summary-btn"
                >
                  Ver Resumen Semanal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-16 mt-0 sm:-mt-8 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 items-stretch">
            <Card
              className="h-full bg-[#FCFAF5] border border-[#D4C5A5]/45 shadow-sm card-hover animate-fade-in-up stagger-1"
              data-testid="stat-beneficiarios"
            >
              <CardContent className="px-3 py-4 sm:p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-[1_1_auto] max-w-[calc(100%-48px)] sm:max-w-none">
                    <p className="text-[12px] sm:text-sm font-medium text-slate-500 uppercase tracking-normal leading-tight">
                      Beneficiarios Totales
                    </p>
                    <p className="text-3xl lg:text-4xl font-bold text-[#8B1C4A] mt-2">
                      {getTotalBeneficiaries(stats).toLocaleString()}
                    </p>
                  </div>

                  <div className="shrink-0 self-start p-1.5 sm:p-3 bg-[#8B1C4A]/10 rounded-lg sm:rounded-xl -mr-1">
                    <Users className="w-6 h-6 sm:w-6 sm:h-6 text-[#8B1C4A]" />
                  </div>
                </div>

                <p className="text-[13px] sm:text-sm text-slate-500 mt-3">
                  <span className="text-green-600 font-medium">
                    +{getWeeklyBeneficiaries(stats).toLocaleString()}
                  </span>{' '}
                  esta semana
                </p>
              </CardContent>
            </Card>

            <Card
              className="h-full bg-[#FCFAF5] border border-[#D4C5A5]/45 shadow-sm card-hover animate-fade-in-up stagger-1"
              data-testid="stat-acciones"
            >
              <CardContent className="px-3 py-4 sm:p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-[1_1_auto] max-w-[calc(100%-48px)] sm:max-w-none">
                    <p className="text-[12px] sm:text-sm font-medium text-slate-500 uppercase tracking-normal leading-tight">
                      Acciones Realizadas
                    </p>
                    <p className="text-3xl lg:text-4xl font-bold text-[#BC955C] mt-2">
                      {getTotalActions(stats).toLocaleString()}
                    </p>
                  </div>

                  <div className="shrink-0 self-start p-1.5 sm:p-3 bg-[#BC955C]/10 rounded-lg sm:rounded-xl -mr-1">
                    <Activity className="w-6 h-6 sm:w-6 sm:h-6 text-[#BC955C]" />
                  </div>
                </div>

                <p className="text-[13px] sm:text-sm text-slate-500 mt-3">
                  <span className="text-green-600 font-medium">
                    +{getWeeklyActions(stats).toLocaleString()}
                  </span>{' '}
                  esta semana
                </p>
              </CardContent>
            </Card>

            <Card
              className="h-full bg-[#FCFAF5] border border-[#D4C5A5]/45 shadow-sm card-hover animate-fade-in-up stagger-1"
              data-testid="stat-territorios"
            >
              <CardContent className="px-3 py-4 sm:p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-[1_1_auto] max-w-[calc(100%-48px)] sm:max-w-none">
                    <p className="text-[12px] sm:text-sm font-medium text-slate-500 uppercase tracking-normal leading-tight">
                      Territorios Activos
                    </p>
                    <p className="text-3xl lg:text-4xl font-bold text-[#235B4E] mt-2">
                      {getCoverageTerritoriesActive(stats)}/{territoriesTotal || 0}
                    </p>
                  </div>

                  <div className="shrink-0 self-start p-1.5 sm:p-3 bg-[#235B4E]/10 rounded-lg sm:rounded-xl -mr-1">
                    <MapPin className="w-6 h-6 sm:w-6 sm:h-6 text-[#235B4E]" />
                  </div>
                </div>

                <p className="text-[13px] sm:text-sm text-slate-500 mt-3">
                  Con actividades registradas
                </p>
              </CardContent>
            </Card>

            <Card
              className="h-full bg-[#FCFAF5] border border-[#D4C5A5]/45 shadow-sm card-hover animate-fade-in-up stagger-1"
              data-testid="stat-dependencias"
            >
              <CardContent className="px-3 py-4 sm:p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-[1_1_auto] max-w-[calc(100%-48px)] sm:max-w-none">
                    <p className="text-[12px] sm:text-sm font-medium text-slate-500 uppercase tracking-normal leading-tight">
                      Dependencias Activas
                    </p>
                    <p className="text-3xl lg:text-4xl font-bold text-[#3F51B5] mt-2">
                      {getCoverageDependenciesActive(stats)}/{dependenciesTotal || 0}
                    </p>
                  </div>

                  <div className="shrink-0 self-start p-1.5 sm:p-3 bg-[#3F51B5]/10 rounded-lg sm:rounded-xl -mr-1">
                    <Building2 className="w-6 h-6 sm:w-6 sm:h-6 text-[#3F51B5]" />
                  </div>
                </div>

                <p className="text-[13px] sm:text-sm text-slate-500 mt-3">
                  Reportando actividades
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 items-stretch">
            <Card className="h-full bg-[#FCFAF5] border border-[#D4C5A5]/45 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <TrendingUp className="w-5 h-5 text-[#8B1C4A]" />
                  Tendencia Semanal
                </CardTitle>
              </CardHeader>

              <CardContent>
                <div className="h-[300px]" data-testid="weekly-trend-chart">
                  {normalizedWeeklyTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={normalizedWeeklyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#D4C5A5" />
                        <XAxis
                          dataKey="week"
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => formatDayMonth(value)}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          labelFormatter={(label) => formatDate(label)}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #D4C5A5',
                            borderRadius: '8px',
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="beneficiarios"
                          stroke="#8B1C4A"
                          strokeWidth={2}
                          name="Beneficiarios"
                          dot={{ fill: '#8B1C4A' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="acciones"
                          stroke="#BC955C"
                          strokeWidth={2}
                          name="Acciones"
                          dot={{ fill: '#BC955C' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-500">
                      No hay datos disponibles
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="h-full bg-[#FCFAF5] border border-[#D4C5A5]/45 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <MapPin className="w-5 h-5 text-[#235B4E]" />
                  Impacto por Territorio
                </CardTitle>
              </CardHeader>

              <CardContent>
                <div className="h-[300px]" data-testid="territory-chart">
                  {territoryChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={territoryChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#D4C5A5" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis
                          dataKey="name"
                          type="category"
                          width={100}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #D4C5A5',
                            borderRadius: '8px',
                          }}
                        />
                        <Bar
                          dataKey="beneficiarios"
                          fill="#8B1C4A"
                          name="Beneficiarios"
                          radius={[0, 4, 4, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-slate-500">
                      No hay datos disponibles
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      
      <section className="py-10 lg:py-14 bg-[#F5F0E1] border-t border-[#D4C5A5]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto rounded-2xl bg-white border border-[#D4C5A5]/40 shadow-sm px-6 py-8 sm:px-10 sm:py-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#8B1C4A] mb-4">
              Explora las Acciones del Gobierno
            </h2>

            <p className="text-slate-600 max-w-2xl mx-auto mb-8">
              Conoce el impacto de las {dependenciesTotal || 'distintas'} dependencias del Gobierno de la Ciudad de México
              en los {territoriesTotal || 'distintos'} territorios estratégicos.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Link to="/territorios">
                <Button className="bg-[#8B1C4A] text-white hover:bg-[#70163A] font-semibold px-8 py-3">
                  Ver Mapa de Territorios
                </Button>
              </Link>

              <Link to="/dependencias">
                <Button
                  variant="outline"
                  className="border-2 border-[#8B1C4A] text-[#8B1C4A] hover:bg-[#8B1C4A]/5 font-semibold px-8 py-3"
                >
                  Ver Dependencias
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;